import { computed, ref, watch, type Ref } from 'vue';
import { useRoute } from 'vue-router';
import { useGlobalState } from './use-global-store';
import type { IExportParams, IReportCardInstance } from '@/types';
import { getProjectInfo, refreshReportInfo, exportReport } from '@/infrastructure/api-functions/report';
import { useDirectory } from './use-directory';

export function useReport() {
    const route = useRoute();
    const isReportLoading = ref(false);
    const isExportLoading = ref(false);
    const isRefreshLoading = ref(false);
    const downloadUrl = ref('');
    const { project, updateProjectInfo } = useGlobalState();
    const { refreshCatalogList } = useDirectory();

    const { isAllFinished } = useDirectory()

    const cardInstanceList: Ref<IReportCardInstance[]> = ref([])

    const canExport = computed(() => {
        return isAllFinished.value && !isReportLoading.value && !isExportLoading.value;
    });

    function updateCardInstances(cardInstances: IReportCardInstance[]) {
        cardInstanceList.value = cardInstances
    }

    async function getExportImages(): Promise<IExportParams> {
        const exportData: IExportParams = { project_id: project.value?.id } as IExportParams;
        for (let i = 0; i < cardInstanceList.value.length; i++) {
            const component = cardInstanceList.value[i];
            const base64Obj = await component.getBase64Str?.() || {};
            Object.assign(exportData, base64Obj);
        }
        return exportData;
    }

    function downloadReport() {
        if (canExport.value) {
            isExportLoading.value = true;
            return getExportImages().then(exportData => {
                return exportData;
            }).then((exportData) => {
                return exportReport(exportData).then(url => {
                    if (url) {
                        window.open(url, '_blank', 'noopener')
                        downloadUrl.value = url;
                    }
                })
            }).finally(() => {
                isExportLoading.value = false;
            });
        }
    }

    /**
     * @returns 常规刷新报告
     */
    function refreshReports(forceRefreshCatalog = false) {
        isRefreshLoading.value = true;
        downloadUrl.value = '';
        if (!project.value?.id) return;
        refreshReportInfo(project.value?.id).then((info) => {
            if (forceRefreshCatalog) {
                refreshCatalogList();
                updateProjectInfo({ lastUpdate: info.data_updated_at, project_version: info.project_version });
                return;
            }
            updateProjectInfo({ lastUpdate: info.data_updated_at });
            cardInstanceList.value.forEach(ins => {
                ins.refreshCard?.();
            });
        }).finally(() => {
            isRefreshLoading.value = false;
        });
    }

    watch(() => route.params.projectId, (v) => {
        isReportLoading.value = true;
        updateProjectInfo({ id: v as string });
        getProjectInfo(v as string).then(projectInfo => {
            updateProjectInfo({
                id: projectInfo.project_id,
                title: projectInfo.project_title,
                icon: projectInfo.icon,
                lastUpdate: projectInfo.data_updated_at,
                project_version: projectInfo.project_version,
            });
        }).finally(() => {
            isReportLoading.value = false;
        });
    }, {
        immediate: true,
    })

    return {
        canExport,
        updateCardInstances,
        refreshReports,
        isReportLoading,
        isExportLoading,
        isRefreshLoading,
        downloadUrl,
        downloadReport,
    }
}
